import { useMemo } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { type HsbcOffer, images } from "@hsbc/portal";
import { Dialog, DialogContent, DialogTrigger, DialogClose } from "@tenant/ui";
import clsx from "clsx";
import styles from "./styles.module.scss";

export interface OfferDialogProps extends HsbcOffer, PlatformComponentProps {
  onOpen?: () => void;
  onClose?: () => void;
}

const getLogo = (provider: HsbcOffer["provider"], textColor: string) => {
  if (provider === "hsbc") {
    return images.hsbcLogoWhite;
  }

  return textColor === "white"
    ? images.partnershipLogoWhite
    : images.partnershipLogoBlack;
};

const getBackgroundGradient = (position: "left" | "right") =>
  position === "right"
    ? "linear-gradient(to left, rgba(0, 0, 0, 0.5) 0%,  rgba(0, 0, 0, 0) 100%,  transparent 70%)"
    : "linear-gradient(to right, rgba(0, 0, 0, 0.5) 0%,  rgba(0, 0, 0, 0) 100%,  transparent 70%)";

export const OfferDialog = ({
  image,
  title,
  description,
  styles: offerStyles,
  provider,
  className,
  children,
  onOpen,
  onClose,
}: OfferDialogProps) => {
  const { t } = useI18nContext();
  const [, cn] = useModuleBEM(styles, "OfferDialog");
  const navigate = useNavigate();

  const { position = "left", textColor = "white" } = offerStyles ?? {};
  const { url: backgroundImage, position: imagePosition } = image ?? {};

  const isBackgroundImage = useMemo(
    () => imagePosition === "background",
    [imagePosition]
  );

  const background =
    backgroundImage != null && isBackgroundImage
      ? `${getBackgroundGradient(position)}, url(${backgroundImage})`
      : "";

  return (
    <Dialog onOpen={onOpen} onClose={onClose} key={title}>
      <DialogTrigger asChild>
        <section
          className={clsx(cn("banner"), className)}
          style={{ backgroundImage: background }}
        >
          <div
            className={cn("content", {
              [`position-${position}`]: true,
              ["inline-images"]: !isBackgroundImage,
            })}
          >
            {provider && (
              <img
                src={getLogo(provider, textColor)}
                alt="HSBC - Hopper logo"
                className={cn("logo")}
              />
            )}
            <span
              style={{ color: textColor }}
              dangerouslySetInnerHTML={{ __html: title }}
            />

            {!isBackgroundImage && (
              <img src={image?.url} className={cn("image")} alt={title} />
            )}
          </div>
        </section>
      </DialogTrigger>

      <DialogContent
        title={description?.title}
        className={cn("dialog-content")}
      >
        {description?.image != null && (
          <img
            src={description?.image}
            className={cn("image")}
            alt={description?.title}
          />
        )}

        {description?.body != null && (
          <span
            className={cn("body")}
            dangerouslySetInnerHTML={{ __html: description.body }}
          />
        )}

        {description?.terms != null && (
          <span
            className={cn("fine-print")}
            dangerouslySetInnerHTML={{ __html: description.terms }}
          />
        )}

        {description?.callToAction?.map((callToAction) => (
          <button
            key={callToAction.label}
            className={cn("dialog-button")}
            onClick={() => {
              const { url } = callToAction;
              if (url.startsWith("http")) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
            }}
          >
            {callToAction.label}
          </button>
        ))}
        {children}

        <DialogClose className={cn("dialog-button")}>
          {t("close.button")}
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
};
